import React from 'react';
import {
  RewardsTextWrapper,
  RewardsWrapper,
  CardContainer,
  Card,
  CardImage,
  StyledCardButtonWrapper,
  CardContent,
  CardContentWrapper,
  StyledCardSection,
  RewardsCtaWrapper,
  CtaBottomWrapper,
  StyledCardHeading,
  DescriptionWrapper
} from './RewardsCarasoule.styled';
import Typography from '@latitude/typography';
import { REBRAND_COLOR } from '@/utils/constants';

const COMPONENT_ID = 'rewards-carasoule';

// const data = {

/**
 * A carousel/grid component that displays a list of rewards with images, titles, descriptions, and CTAs.
 * Optionally shows a main CTA at the bottom.
 *
 * @param {Object} props - Component props
 * @param {string} [props.title] - Main title for the rewards section
 * @param {string} [props.subtitle] - Subtitle for the rewards section
 * @param {Array<{imageUrl?: string, title?: string, description?: string, cta?: React.ReactNode}>} [props.rewards=[]] - Array of reward items to display
 * @param {React.ReactNode} [props.rewardsCta] - CTA button or element displayed at the bottom
 * @param {string} [props.trackingId] - Optional tracking ID prefix for analytics
 * @param {Object} [rest] - Additional props spread to the root element
 *

 * @example
 * // Example usage:
 * const data = {
 *   title: 'Experience more with Latitude Rewards',
 *   subtitle: 'Each Latitude Reward is worth $1, no point conversion needed',
 *   rewards: [
 *     {
 *       imageUrl: 'https://images.ctfassets.net/w0q68lqdeo57/2jo5xxgMm6oQwAKvVnL58Q/dc0df99453d6b9addabf05b9bdb6bbc6/pl-promo-money-Img-home-page.png',
 *       title: 'Earn on everyday spends',
 *       description: 'Get 3% back on recurring payments with participating utilities, telco and streaming services.',
 *       cta: (<Button brandInverse autoWidth>Learn More</Button>)
 *     },
 *     {
 *       imageUrl: 'https://images.ctfassets.net/w0q68lqdeo57/2jo5xxgMm6oQwAKvVnL58Q/dc0df99453d6b9addabf05b9bdb6bbc6/pl-promo-money-Img-home-page.png',
 *       title: 'Rewards Carousel Image 1',
 *       description: 'Redeem rewards for gift cards from popular brands',
 *       cta: (<Button brandInverse autoWidth>Learn More</Button>)
 *     }
 *   ],
 *   rewardsCta: (<Button brandInverse autoWidth>Learn More</Button>)
 * };
 *
 * <RewardsCarasoule
 *   title={data.title}
 *   subtitle={data.subtitle}
 *   rewards={data.rewards}
 *   rewardsCta={data.rewardsCta}
 * />
 *
 * @returns {JSX.Element} The rendered RewardsCarasoule component
 */

export const RewardsCarasoule = ({
  title,
  subtitle,
  rewards,
  rewardsCta,
  bottomDescription,
  trackingId,
  ...rest
}) => {
  const componentIdentifier = trackingId
    ? `${trackingId}-${COMPONENT_ID}`
    : `${COMPONENT_ID}`;

  return (
    <RewardsWrapper className={`${componentIdentifier}-wrapper`} {...rest}>
      <RewardsTextWrapper className={`${componentIdentifier}-text-wrapper`}>
        {title && <Typography.H4>{title}</Typography.H4>}
        {subtitle && <Typography.Body color={REBRAND_COLOR.INDIGO}>{subtitle}</Typography.Body>}
      </RewardsTextWrapper>

      <StyledCardSection className={`${componentIdentifier}-card-section`}>
        {rewards?.length > 0 && (
          <CardContainer
            length={rewards.length}
            className={`${componentIdentifier}-card-container`}
          >
            {rewards.map((reward, index) => (
              <Card
                key={`reward-card-${index}`}
                className={`${componentIdentifier}-card-item`}
              >
                {reward.imageUrl && (
                  <CardImage
                    src={reward.imageUrl}
                    alt={reward.title || `rewards image ${index + 1}`}
                  />
                )}

                <CardContentWrapper>
                  <CardContent>
                    {reward.title && <StyledCardHeading className={`${componentIdentifier}-card-title`}><Typography.SH4 semibold>{reward.title}</Typography.SH4></StyledCardHeading>}
                    {reward.description && (
                      <Typography.Body small color={REBRAND_COLOR.INDIGO}>{reward.description}</Typography.Body>
                    )}
                  </CardContent>

                  {/* BUTTON pinned to bottom */}
                  {reward.cta && (
                    <StyledCardButtonWrapper>
                      {reward.cta}
                    </StyledCardButtonWrapper>
                  )}
                </CardContentWrapper>
              </Card>
            ))}
          </CardContainer>
        )}
      </StyledCardSection>

      {bottomDescription && (
        <DescriptionWrapper className={`${componentIdentifier}-description`}>
          <Typography.Body small color={REBRAND_COLOR.INDIGO}>{bottomDescription}</Typography.Body>
        </DescriptionWrapper>
      )}

      <CtaBottomWrapper
        className={`${componentIdentifier}-card-bottom-wrapper`}
      >
        {rewardsCta && <RewardsCtaWrapper>{rewardsCta}</RewardsCtaWrapper>}
      </CtaBottomWrapper>
    </RewardsWrapper>
  );
};
